import { ulid } from 'ulid';

import { DEFAULT_SYMBOLOGY, defaultModule, defaultQuiet, isAspectLocked, moduleFloor, pruneCodeForSymbology } from '../../utils/barcode/symbologies';
import { blockPage, blocksOnSheet, formSheets, offFormBlocks, sheetPage } from './pageLayoutForm';
import { defaultGroupFields, scaleGroup } from './pageGroupLines';

/**
 * The pure model behind the page-layout editor — the displayTemplateModel /
 * tileLayoutModel split: every rule is here and unit-tested, so the canvas
 * component only has to draw and drag.
 *
 * Coordinates are in the PAGE's own unit throughout. A CSS inch is 96px by
 * definition and a millimetre is 96/25.4, so a drag delta in pixels converts
 * to page units exactly, with no measuring of the DOM and no scale to thread
 * through — which is what makes dragging testable as arithmetic.
 *
 * A block on a form (KPR-1860) is kept inside ITS sheet and aligned with that
 * sheet's blocks: `sheetPage` and `blocksOnSheet` (pageLayoutForm.js) answer
 * that, and with no form they answer `body.page` and every block.
 */

export const PAGE_SIZES = {
	letter: { width: 8.5, height: 11, unit: 'in' },
	legal: { width: 8.5, height: 14, unit: 'in' },
	tabloid: { width: 11, height: 17, unit: 'in' },
	a4: { width: 210, height: 297, unit: 'mm' },
	a5: { width: 148, height: 210, unit: 'mm' },
	'label-4x6': { width: 4, height: 6, unit: 'in' },
	'label-2x1': { width: 2, height: 1, unit: 'in' },
};

// Kept in lock-step with keepr-api utils/cardLayouts/validate.js BLOCK_KINDS.
// The two lists drift with nothing to catch it, so
// __tests__/blockKindParity.test.js reads the API's copy when ../keepr-api is
// checked out and skips when it is not — the vendored-test idiom.
export const BLOCK_KINDS = ['element', 'text', 'badge', 'rule', 'query', 'barcode', 'group'];

/** The kinds placed from Fields, by the element they show. The rest are the palette's Blocks. */
export const FIELD_BLOCK_KINDS = ['element', 'group'];

/** CSS px per page unit. An inch is 96px by definition; a mm is 96/25.4. */
export const pxPerUnit = (unit) => (unit === 'mm' ? 96 / 25.4 : 96);

/**
 * The finest a drag lands without a grid: 1/16in and 0.5mm. Fine enough to
 * place against a form, coarse enough that a drag lands somewhere round
 * rather than on 0.5133in.
 */
export const resolution = unit => (unit === 'mm' ? 0.5 : 1 / 16);

/** Pixels -> page units, rounded to a resolution an author can actually hit. */
export const pxToUnits = (px, unit) => {
	const raw = px / pxPerUnit(unit);
	const step = resolution(unit);
	return Math.round(raw / step) * step;
};

/**
 * THE GRID (KPR-345). Three sizes per unit, ruled by the owner on 2026-10-09:
 * 1/16, 1/4 and 1/2 in; 1 mm, 5 mm and 1 cm. The setting is a SIZE name, not a
 * number, so a sheet that changes unit keeps the choice meaningful ("medium" is
 * 1/4in on a letter sheet and 5mm on an A4). Every step is exactly
 * representable (a power of two in inches, a whole number of mm), so a snapped
 * coordinate never carries float noise into the stored body.
 *
 * `null` is "no grid" everywhere a step is taken below, and means today's
 * behaviour exactly: positions round to `resolution(unit)` and nothing else.
 */
export const GRID_SIZES = ['fine', 'medium', 'coarse'];
export const DEFAULT_GRID_SIZE = 'medium';

const GRID_STEPS = {
	in: { fine: 1 / 16, medium: 1 / 4, coarse: 1 / 2 },
	mm: { fine: 1, medium: 5, coarse: 10 },
};
const GRID_LABELS = {
	in: { fine: '1/16 in', medium: '1/4 in', coarse: '1/2 in' },
	mm: { fine: '1 mm', medium: '5 mm', coarse: '1 cm' },
};

/** The grid's spacing in the page's own unit. */
export const gridStep = (unit, size = DEFAULT_GRID_SIZE) => {
	const steps = GRID_STEPS[unit === 'mm' ? 'mm' : 'in'];
	return steps[size] || steps[DEFAULT_GRID_SIZE];
};

/** What a grid size is called to the person, in the page's unit. */
export const gridLabel = (unit, size = DEFAULT_GRID_SIZE) => {
	const labels = GRID_LABELS[unit === 'mm' ? 'mm' : 'in'];
	return labels[size] || labels[DEFAULT_GRID_SIZE];
};

/**
 * How far one keyboard nudge moves a block, in page units: a grid step while
 * snap is on, otherwise the finest the page offers. The one answer KPR-348's
 * arrow-key nudging asks, so a nudge and a drag can never disagree about where
 * the grid is.
 */
export const nudgeStep = (unit, snap, size = DEFAULT_GRID_SIZE) => (snap ? gridStep(unit, size) : resolution(unit));

/**
 * Shift with an arrow moves a block this many nudges at once (KPR-348). Four
 * whole steps, so with snap on a Shift-nudge is still a multiple of the grid:
 * 1 in on the default 1/4 in grid, 20 mm on the 5 mm one.
 */
export const SHIFT_NUDGE_FACTOR = 4;
export const shiftNudgeStep = (unit, snap, size = DEFAULT_GRID_SIZE) => nudgeStep(unit, snap, size) * SHIFT_NUDGE_FACTOR;

/**
 * The spacing the grid is DRAWN at: the grid's own, doubled until the lines
 * are at least 8px apart. A 1/16in grid at 6px would be a grey wash, not a
 * guide; every line drawn is still a grid line, only fewer of them.
 */
export const gridDrawStep = (unit, step) => {
	let drawn = step;
	while (drawn * pxPerUnit(unit) < 8) drawn *= 2;
	return drawn;
};

/** The nearest grid line. */
export const snapTo = (value, step) => (step ? Math.round(value / step) * step : value);

// The epsilon keeps 10.999999999999998 from dropping a whole grid line.
const floorTo = (value, step) => Math.floor(value / step + 1e-9) * step;
const ceilTo = (value, step) => Math.ceil(value / step - 1e-9) * step;

/**
 * A point in pixels from the sheet's top-left corner -> page units, or null
 * when it lies off the sheet. A palette drop uses it to land where the pointer
 * was released; null is "not over the page", which the caller treats as the
 * default placement rather than a position, because clamping a release over
 * the sidebar onto the page edge would look like a drop the author never made.
 * With a grid the point goes to the nearest grid line straight from the
 * pixels: rounding to 1/16in first would put a point near the middle of two
 * 1/4in lines on the wrong one.
 */
export function pointToUnits(xPx, yPx, page, step = null, { exact = false } = {}) {
	const w = page.width * pxPerUnit(page.unit);
	const h = page.height * pxPerUnit(page.unit);
	if (!(xPx >= 0 && yPx >= 0 && xPx <= w && yPx <= h)) return null;
	// The guides (KPR-348) are measured from the pointer itself: rounding it to
	// 1/16 in first is as large as the guide threshold.
	if (exact) return { x: xPx / pxPerUnit(page.unit), y: yPx / pxPerUnit(page.unit) };
	if (step) {
		const ppu = pxPerUnit(page.unit);
		return { x: snapTo(xPx / ppu, step), y: snapTo(yPx / ppu, step) };
	}
	return { x: pxToUnits(xPx, page.unit), y: pxToUnits(yPx, page.unit) };
}

/**
 * The arrow-key getter for a keyboard drag, one grid step per press. dnd-kit's
 * own moves 25px, which is no multiple of any grid, so with snap on a grabbed
 * block would land between lines after every press and only settle on release.
 */
export const gridCoordinateGetter = stepPx => (event, { currentCoordinates }) => {
	switch (event.code) {
		case 'ArrowRight': return { ...currentCoordinates, x: currentCoordinates.x + stepPx };
		case 'ArrowLeft': return { ...currentCoordinates, x: currentCoordinates.x - stepPx };
		case 'ArrowDown': return { ...currentCoordinates, y: currentCoordinates.y + stepPx };
		case 'ArrowUp': return { ...currentCoordinates, y: currentCoordinates.y - stepPx };
		default: return undefined;
	}
};

const round = (value, unit) => {
	const places = unit === 'mm' ? 1 : 4;
	return Number(Number(value).toFixed(places));
};

/**
 * Rounded to the sheet's resolution, but never past `limit`. A form's sheet is
 * sized finer than a coordinate (two places in mm, where a coordinate keeps one),
 * so rounding to the nearest could put a block's far edge off the sheet, where the
 * API refuses it.
 */
const roundWithin = (value, limit, unit) => {
	const rounded = round(value, unit);
	if (rounded <= limit + 1e-9) return rounded;
	const places = unit === 'mm' ? 10 : 10000;
	return Math.floor(value * places + 1e-9) / places;
};

/**
 * The X-dimension is a FINER quantity than a coordinate and needs its own
 * precision: the matrix floor is 0.25mm, and rounding to the sheet's own
 * 0.1mm would quantise a third of the usable range away — 0.254mm would
 * become 0.3, which is an 18% wider symbol than the author set.
 */
const roundModule = (value, unit) => Number(Number(value).toFixed(unit === 'mm' ? 3 : 5));

export const emptyPage = (size = 'letter') => ({
	size,
	...PAGE_SIZES[size],
	orientation: 'portrait',
	margins: PAGE_SIZES[size].unit === 'mm'
		? { top: 12, right: 12, bottom: 12, left: 12 }
		: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 },
});

export const emptyBody = () => ({ subject: 'record', page: emptyPage(), blocks: [] });

const clone = body => ({ ...body, page: { ...body.page, margins: { ...body.page.margins } }, blocks: body.blocks.map(b => ({ ...b, style: { ...(b.style || {}) } })) });

/** The default size of a newly dropped block, in the page's unit. */
function defaultSize(type, page) {
	const inches = {
		element: { w: 2.2, h: 0.5 },
		text: { w: 4, h: 0.4 },
		badge: { w: 3, h: 0.5 },
		rule: { w: 4, h: 0 },
		query: { w: 6, h: 3 },
		// A square inch: big enough that a QR at the default X-dimension has
		// room, small enough to sit on a 2x1 label without covering it.
		barcode: { w: 1, h: 1 },
		// A table's area, redrawn over the form's own in a drag or two.
		group: { w: 6, h: 3 },
	}[type] || { w: 2, h: 0.5 };
	const scale = page.unit === 'mm' ? 25.4 : 1;
	return { w: roundWithin(Math.min(inches.w * scale, page.width), page.width, page.unit), h: round(inches.h * scale, page.unit) };
}

/** The smallest a block may be, in the page's unit. */
const minSize = unit => (unit === 'mm' ? 2 : 0.1);

/**
 * Adds a block. A block is clamped INTO the page horizontally, because a
 * block hanging off the side is refused by the API and would be lost work;
 * vertically it may sit anywhere above the bottom, since running past the
 * bottom is how a page breaks.
 *
 * With a grid `step`, everything the editor places lands on a grid line: the
 * position (a click's default one included) and the default size, so a block
 * dropped from the palette is not the one block on the sheet that is off the
 * grid.
 */
export function addBlock(body, { type, element = null, group = null, x = null, y = null, step = null, formPage = 1 }) {
	const next = clone(body);
	const page = sheetPage(next, formPage);
	let { w, h } = defaultSize(type, page);
	if (step) {
		w = round(Math.max(ceilTo(minSize(page.unit), step), Math.min(snapTo(w, step), floorTo(page.width, step))), page.unit);
		// A rule has a width and no height; its 0 is not a size to snap up.
		if (type !== 'rule') h = round(Math.max(ceilTo(minSize(page.unit), step), snapTo(h, step)), page.unit);
	}
	const left = clampX(x === null ? snapTo(page.margins.left, step) : snapTo(x, step), w, page, step);
	// Stacked UNDER what is already there, not on top of it: dropping three
	// fields in a row and finding one block with two hidden beneath it is the
	// kind of thing an author only discovers after saving.
	const top = clampY(y === null ? nextFreeY(next, page, step, formPage) : snapTo(y, step), page, step);
	const block = {
		id: ulid(),
		type,
		x: left,
		y: top,
		w,
		...(type === 'rule' ? {} : { h }),
		style: {},
		// Page 1 is stored absent (keepr-api docs/SCHEMA.md).
		...(formPage > 1 ? { formPage } : {}),
		...((type === 'element' || type === 'group') && element ? { element } : {}),
		...(type === 'group' ? defaultGroupFields(group, w, page.unit) : {}),
		...(type === 'text' ? { template: '' } : {}),
		...(type === 'query' ? { query: '', render: 'list' } : {}),
		// A new barcode encodes the record itself — the overwhelmingly common
		// payload, and the one an author would otherwise have to know the
		// token grammar to write. Module and quiet are STORED, the way the
		// API stores them, so what the editor shows is what will print.
		...(type === 'barcode' ? {
			symbology: DEFAULT_SYMBOLOGY,
			payload: '{{_url}}',
			module: defaultModule(page.unit),
			quiet: defaultQuiet(DEFAULT_SYMBOLOGY),
			rotate: 'N',
		} : {}),
	};
	next.blocks.push(block);
	return next;
}

/**
 * Where the next block goes when the author did not say: just below the
 * lowest one, or back at the top margin once the sheet is full — a new block
 * off the bottom of a long page would be worse than one that overlaps.
 */
function nextFreeY(body, page, step = null, formPage = 1) {
	const gap = page.unit === 'mm' ? 3 : 0.125;
	const lowest = blocksOnSheet(body, formPage).reduce(
		(bottom, block) => Math.max(bottom, block.y + (block.h || 0)),
		null
	);
	if (lowest === null) return snapTo(page.margins.top, step);
	const y = lowest + gap;
	// UP to the next line, never to the nearest: rounding down could put the
	// new block back over the one it is meant to sit under.
	const placed = step ? ceilTo(y, step) : y;
	return placed >= page.height - page.margins.bottom ? snapTo(page.margins.top, step) : placed;
}

// With a grid `step` the far limit is the last grid line that still fits, so a
// snapped block is never past the edge a hair to keep a multiple.
export const clampX = (x, w, page, step = null) => {
	const far = page.width - w;
	return roundWithin(Math.max(0, Math.min(x, step ? floorTo(far, step) : far)), far, page.unit);
};
// A hair inside the bottom edge: a block AT the height is refused by the API.
export const clampY = (y, page, step = null) => {
	const far = page.height - 0.01;
	return roundWithin(Math.max(0, Math.min(y, step ? floorTo(far, step) : far)), far, page.unit);
};

export function removeBlock(body, id) {
	const next = clone(body);
	next.blocks = next.blocks.filter(block => block.id !== id);
	return next;
}

/**
 * A pixel delta in page units. Without a grid it is rounded to the sheet's
 * resolution; with one it is left exact, because setBlockBox snaps the SUM
 * (where the block ends up), not the delta (how far it was carried) — an
 * off-grid block carried a hair must land on a line, not move to another
 * off-grid spot.
 */
const deltaUnits = (px, unit, step) => (step ? px / pxPerUnit(unit) : pxToUnits(px, unit));

/**
 * Moves a block by a PIXEL delta — what a drag hands back. With a grid `step`
 * the block's x and y land on grid lines.
 */
export function moveBlockByPx(body, id, dxPx, dyPx, step = null) {
	const block = body.blocks.find(b => b.id === id);
	if (!block) return body;
	const { page } = body;
	return setBlockBox(body, id, {
		x: block.x + deltaUnits(dxPx, page.unit, step),
		y: block.y + deltaUnits(dyPx, page.unit, step),
	}, step);
}

/**
 * Resizes a block by a PIXEL delta — what a corner handle hands back. With a
 * grid `step` its width and height land on grid lines.
 */
export function resizeBlockByPx(body, id, dwPx, dhPx, step = null) {
	const block = body.blocks.find(b => b.id === id);
	if (!block) return body;
	const { page } = body;
	return setBlockBox(body, id, {
		w: block.w + deltaUnits(dwPx, page.unit, step),
		...(block.type === 'rule' ? {} : { h: (block.h || 0) + deltaUnits(dhPx, page.unit, step) }),
	}, step);
}

/**
 * Carries a block `amount` page units in a direction (KPR-348's arrow keys):
 * `dx` / `dy` are -1, 0 or 1. Without a grid the block moves exactly `amount`.
 * With one it goes to a grid LINE: the next one in the direction pressed, so a
 * block that sits between lines goes to the line ahead of it (never back, never
 * nowhere), and one on a line goes `amount` further (a multiple of the grid,
 * see shiftNudgeStep). Only the axis pressed moves; the page edge is the usual
 * clamp, so a nudge against it changes nothing.
 */
export function nudgeBlock(body, id, { dx = 0, dy = 0 }, { amount, step = null }) {
	const block = body.blocks.find(b => b.id === id);
	if (!block || !(amount > 0)) return body;
	const along = (value, direction) => {
		if (!direction) return undefined;
		if (!step) return value + direction * amount;
		const onLine = Math.abs(value / step - Math.round(value / step)) < 1e-9;
		if (onLine) return Math.round(value / step) * step + direction * amount;
		return direction > 0 ? floorTo(value, step) + amount : ceilTo(value, step) - amount;
	};
	const x = along(block.x, dx);
	const y = along(block.y, dy);
	const moved = setBlockBox(body, id, { ...(x === undefined ? {} : { x }), ...(y === undefined ? {} : { y }) }, step);
	// A clamp can pull a block BACK: one the page edge left off the grid (dragged
	// there with snap off) has the last grid line behind it. A nudge never goes
	// the wrong way; against the edge it stays put on that axis.
	const after = moved.blocks.find(b => b.id === id);
	const wrongWay = (from, to, direction) => direction && (direction > 0 ? to < from : to > from);
	const keepX = wrongWay(block.x, after.x, dx);
	const keepY = wrongWay(block.y, after.y, dy);
	if (!keepX && !keepY) return moved;
	return setBlockBox(body, id, {
		...(x === undefined || keepX ? {} : { x }),
		...(y === undefined || keepY ? {} : { y }),
	}, step);
}

// How close counts as "on the guide", in page units. Never as much as the grid:
// on the finest grid (1/16 in is 6px) a keyboard carry one step from a guide
// would otherwise snap to it too, and two presses in a row would do nothing.
const guideTolerance = (unit, step) => {
	const base = GUIDE_PX / pxPerUnit(unit);
	return step ? Math.min(base, step * 0.49) : base;
};

/**
 * ALIGNMENT GUIDES (KPR-348). While a block is carried (or sized) its left,
 * centre and right edges - top, middle and bottom - are compared with the same
 * edges of every OTHER block and with the page margins; within `GUIDE_PX`
 * pixels of one, the block snaps to it on that axis and the line is drawn.
 *
 * Decisions:
 * - The guide beats the grid. A margin of 12 mm sits between the lines of a 5 mm
 *   grid, and "snaps to the guide" would mean nothing if the grid pulled it back.
 *   Away from every guide the grid is exactly as KPR-345 left it.
 * - The pixel threshold is measured on the RAW pointer position, not on the
 *   grid-snapped one: on a 1/2 in grid the snapped block can sit 24 px from
 *   a guide the pointer is touching.
 * - The lines drawn are worked out from the block as it ENDS UP (after the
 *   clamp and the rounding to the sheet's resolution), so a guide is never
 *   drawn where the block is not.
 * - A margin is matched by a block's edges only: a centre on a margin line is
 *   a coincidence, not an alignment.
 * - A resize aligns only the edge being dragged (the right and the bottom).
 */
export const GUIDE_PX = 6;
export const NO_GUIDES = Object.freeze({ x: Object.freeze([]), y: Object.freeze([]) });

// How close is "on the line" for drawing it: the sheet rounds a coordinate to
// 4 places in inches, 1 in mm, so the block can sit that far from the exact value.
const guideEpsilon = unit => (unit === 'mm' ? 0.06 : 0.0002);

const heightOf = block => (block.type === 'rule' ? 0 : block.h || 0);

function guideTargets(body, selfId) {
	const formPage = blockPage(body.blocks.find(block => block.id === selfId));
	const page = sheetPage(body, formPage);
	const x = [];
	const y = [];
	for (const block of blocksOnSheet(body, formPage)) {
		if (block.id === selfId) continue;
		const h = heightOf(block);
		x.push({ at: block.x }, { at: block.x + block.w / 2 }, { at: block.x + block.w });
		y.push({ at: block.y }, { at: block.y + h / 2 }, { at: block.y + h });
	}
	const margins = page.margins || {};
	x.push({ at: margins.left ?? 0, margin: true }, { at: page.width - (margins.right ?? 0), margin: true });
	y.push({ at: margins.top ?? 0, margin: true }, { at: page.height - (margins.bottom ?? 0), margin: true });
	return { x, y };
}

// The smallest move that puts one of `offsets` (from `base`) on a target, or null.
function alignAxis(offsets, base, targets, tolerance) {
	let best = null;
	for (const { o, centre } of offsets) {
		for (const target of targets) {
			if (centre && target.margin) continue;
			const delta = target.at - (base + o);
			if (Math.abs(delta) <= tolerance && (best === null || Math.abs(delta) < Math.abs(best))) best = delta;
		}
	}
	return best;
}

const spanOffsets = size => [{ o: 0 }, { o: size / 2, centre: true }, { o: size }];

/** The lines a block sits on right now: `{ x: [..], y: [..] }` in page units. */
export function alignmentGuides(body, id, { only = 'all' } = {}) {
	const block = body.blocks.find(b => b.id === id);
	if (!block) return NO_GUIDES;
	const eps = guideEpsilon(body.page.unit);
	const targets = guideTargets(body, id);
	const lines = (start, size, list) => {
		const offsets = only === 'far' ? [{ o: size }] : spanOffsets(size);
		const found = new Set();
		for (const { o, centre } of offsets) {
			for (const target of list) {
				if (centre && target.margin) continue;
				if (Math.abs(start + o - target.at) <= eps) found.add(round(target.at, body.page.unit));
			}
		}
		return [...found].sort((a, b) => a - b);
	};
	const x = lines(block.x, block.w, targets.x);
	const y = lines(block.y, heightOf(block), targets.y);
	return x.length || y.length ? { x, y } : NO_GUIDES;
}

/** moveBlockByPx that snaps to alignment guides: `{ body, guides }`. */
export function moveBlockAligned(body, id, dxPx, dyPx, step = null, { align = true } = {}) {
	const block = body.blocks.find(b => b.id === id);
	const moved = moveBlockByPx(body, id, dxPx, dyPx, step);
	if (!block || !align) return { body: moved, guides: NO_GUIDES };
	const ppu = pxPerUnit(body.page.unit);
	const tolerance = guideTolerance(body.page.unit, step);
	const targets = guideTargets(body, id);
	const rawX = block.x + dxPx / ppu;
	const rawY = block.y + dyPx / ppu;
	const sx = alignAxis(spanOffsets(block.w), rawX, targets.x, tolerance);
	const sy = alignAxis(spanOffsets(heightOf(block)), rawY, targets.y, tolerance);
	const next = sx === null && sy === null
		? moved
		: setBlockBox(moved, id, { ...(sx === null ? {} : { x: rawX + sx }), ...(sy === null ? {} : { y: rawY + sy }) }, null);
	return { body: next, guides: alignmentGuides(next, id) };
}

/** resizeBlockByPx that snaps the dragged edges to alignment guides. */
export function resizeBlockAligned(body, id, dwPx, dhPx, step = null, { align = true } = {}) {
	const block = body.blocks.find(b => b.id === id);
	const sized = resizeBlockByPx(body, id, dwPx, dhPx, step);
	if (!block || !align) return { body: sized, guides: NO_GUIDES };
	const ppu = pxPerUnit(body.page.unit);
	const tolerance = guideTolerance(body.page.unit, step);
	const targets = guideTargets(body, id);
	const rawW = block.w + dwPx / ppu;
	const rawH = heightOf(block) + dhPx / ppu;
	const sw = alignAxis([{ o: 0 }], block.x + rawW, targets.x, tolerance);
	const sh = block.type === 'rule' ? null : alignAxis([{ o: 0 }], block.y + rawH, targets.y, tolerance);
	const next = sw === null && sh === null
		? sized
		: setBlockBox(sized, id, { ...(sw === null ? {} : { w: rawW + sw }), ...(sh === null ? {} : { h: rawH + sh }) }, null);
	return { body: next, guides: alignmentGuides(next, id, { only: 'far' }) };
}

/**
 * addBlock for a block let go at a point, snapping to guides as a carried one
 * does. `at` is the grid-snapped point (what addBlock takes) and `rawAt` the
 * same point with no grid, which is what the guides are measured from.
 * `{ body, blockId, guides }`.
 */
export function placeBlockAligned(body, spec, { at = null, rawAt = null, step = null, align = true } = {}) {
	const added = addBlock(body, { ...spec, ...(at || {}), step });
	const block = added.blocks[added.blocks.length - 1];
	if (!align || !rawAt) return { body: added, blockId: block.id, guides: NO_GUIDES };
	return { blockId: block.id, ...alignLanded(added, block, rawAt, step) };
}

/** A block just put at `rawAt`, snapped to the guides of the sheet it is on. */
function alignLanded(body, block, rawAt, step) {
	const tolerance = guideTolerance(body.page.unit, step);
	const targets = guideTargets(body, block.id);
	const sx = alignAxis(spanOffsets(block.w), rawAt.x, targets.x, tolerance);
	const sy = alignAxis(spanOffsets(heightOf(block)), rawAt.y, targets.y, tolerance);
	const next = sx === null && sy === null
		? body
		: setBlockBox(body, block.id, { ...(sx === null ? {} : { x: rawAt.x + sx }), ...(sy === null ? {} : { y: rawAt.y + sy }) }, null);
	return { body: next, guides: alignmentGuides(next, block.id) };
}

/**
 * Puts a block on another sheet of the form, keeping it inside that sheet. Page
 * 1 is stored absent.
 */
export function setBlockPage(body, id, formPage) {
	const next = clone(body);
	next.blocks = next.blocks.map(block => {
		if (block.id !== id) return block;
		const { formPage: _from, ...rest } = block;
		return formPage > 1 ? { ...rest, formPage } : rest;
	});
	return setBlockBox(next, id, {});
}

/**
 * A block carried onto another sheet and let go at `at` (grid-snapped; `rawAt`
 * is the same point with no grid, which the guides are measured from). The
 * preview and the drop both call this, so the ghost shows where it lands.
 * `{ body, guides }`.
 */
export function moveBlockToSheet(body, id, formPage, { at, rawAt = null, step = null, align = true }) {
	const moved = setBlockBox(setBlockPage(body, id, formPage), id, at, step);
	const block = moved.blocks.find(b => b.id === id);
	if (!block || !align || !rawAt) return { body: moved, guides: NO_GUIDES };
	return alignLanded(moved, block, rawAt, step);
}

/**
 * Every block a replaced form left off it, put back on: onto the last page if
 * its own has gone, and inside its sheet. One step, so undo takes them all back.
 * A block the form can still draw does not move.
 */
export function bringBlocksOntoForm(body) {
	const last = formSheets(body).length;
	return offFormBlocks(body).reduce((latest, { id, formPage }) => setBlockPage(latest, id, Math.min(formPage, last)), body);
}

/**
 * Sets any of x/y/w/h, keeping the block legal: never narrower than one
 * step, never wider than the page, never hanging off the side, never above
 * the top or past the bottom.
 *
 * With a grid `step`, each value in `box` is snapped to a grid line and the
 * limits move onto the grid with it (a size never below one line, a far edge
 * never past the last line). Only what `box` names is snapped: a move leaves
 * the block's size alone and a resize leaves its corner, so carrying an
 * old, off-grid block never resizes it. The inspector's typed numbers call
 * this WITHOUT a step: what a person types is what is stored.
 *
 * A group block's columns and lines scale with its box, from `from` when it is given:
 * the block as it stood before a typed number, so each keystroke scales the block the
 * person began with and not the last keystroke's rounded result (KPR-1863).
 */
export function setBlockBox(body, id, box, step = null, from = null) {
	const next = clone(body);
	const min = minSize(next.page.unit);
	const sizeMin = step ? ceilTo(min, step) : min;
	const sized = box.w !== undefined || box.h !== undefined;
	next.blocks = next.blocks.map(block => {
		if (block.id !== id) return block;
		const page = sheetPage(next, blockPage(block));
		const sizeMax = step ? floorTo(page.width, step) : page.width;
		const w = box.w === undefined
			? roundWithin(Math.max(min, Math.min(block.w, page.width)), page.width, page.unit)
			: roundWithin(Math.max(sizeMin, Math.min(snapTo(box.w, step), sizeMax)), page.width, page.unit);
		const merged = { ...block, w };
		if (block.type !== 'rule') {
			merged.h = box.h === undefined
				? round(Math.max(min, block.h), page.unit)
				: round(Math.max(sizeMin, snapTo(box.h, step)), page.unit);
		}
		// A MATRIX symbology is square by construction, so a drag that
		// squashed it into a rectangle would either distort the symbol or
		// leave the box lying about what will print. The box follows the
		// symbol instead: whichever edge the author moved, the other
		// matches it. (A linear or stacked code has a free height — bar
		// height is sweep tolerance, not data — and is left alone.)
		if (block.type === 'barcode' && isAspectLocked(block.symbology, block.code)) {
			const led = box.w !== undefined && box.h === undefined ? merged.w
				: box.h !== undefined && box.w === undefined ? merged.h
				: Math.min(merged.w, merged.h);
			const side = sized
				? roundWithin(Math.max(sizeMin, Math.min(led, sizeMax)), page.width, page.unit)
				: roundWithin(Math.max(min, Math.min(led, page.width)), page.width, page.unit);
			merged.w = side;
			merged.h = side;
		}
		// A corner the caller did not name stays where it is, grid or no grid
		// (it only moves if it has to, to stay on the page).
		merged.x = box.x === undefined ? clampX(block.x, merged.w, page) : clampX(snapTo(box.x, step), merged.w, page, step);
		merged.y = box.y === undefined ? clampY(block.y, page) : clampY(snapTo(box.y, step), page, step);
		// A group's columns and lines scale with its area (KPR-1863).
		return block.type === 'group' ? { ...scaleGroup(from || block, merged.w, merged.h, page.unit), x: merged.x, y: merged.y } : merged;
	});
	return next;
}

/** Patches one block's own fields (element, template, query, render, …). */
export function setBlockProps(body, id, props) {
	const next = clone(body);
	next.blocks = next.blocks.map(block => {
		if (block.id !== id) return block;
		const merged = { ...block, ...props };
		if (merged.type === 'barcode' && props.symbology !== undefined && props.symbology !== block.symbology) {
			// A `code` key the new symbology does not own is a 400 on save —
			// an eclevel does not survive a move to Code 39 — so it is
			// dropped here rather than left as unsaveable work. The quiet
			// zone follows too, unless the author had already moved it off
			// the old default.
			const pruned = pruneCodeForSymbology(merged.code, merged.symbology);
			if (pruned) merged.code = pruned; else delete merged.code;
			if (block.quiet === defaultQuiet(block.symbology)) merged.quiet = defaultQuiet(merged.symbology);
			// And the module floor: a QR cell needs more room than a bar, so
			// moving a Code 128 to a QR can leave the X-dimension under the
			// new floor, which the API refuses.
			const floor = moduleFloor(merged.symbology, next.page.unit);
			if (!(merged.module >= floor)) merged.module = floor;
		}
		return merged;
	});
	return next;
}

/** Patches one block's style, dropping keys set back to nothing. */
export function setBlockStyle(body, id, style) {
	const next = clone(body);
	next.blocks = next.blocks.map(block => {
		if (block.id !== id) return block;
		const merged = { ...(block.style || {}), ...style };
		Object.keys(merged).forEach(key => {
			if (merged[key] === undefined || merged[key] === null || merged[key] === '') delete merged[key];
		});
		return { ...block, style: merged };
	});
	return next;
}

/**
 * Changes the sheet. Every block is brought back inside the new width, since
 * a narrower sheet would otherwise leave blocks hanging off the side — which
 * the API refuses, so the editor would be holding unsaveable work.
 */
export function setPage(body, patch) {
	const next = clone(body);
	let page = { ...next.page, ...patch };

	if (patch.size && patch.size !== 'custom' && PAGE_SIZES[patch.size]) {
		const named = PAGE_SIZES[patch.size];
		page = { ...page, ...named };
		// A unit change carries the margins with it rather than leaving 0.5mm.
		if (named.unit !== next.page.unit) {
			page.margins = named.unit === 'mm'
				? { top: 12, right: 12, bottom: 12, left: 12 }
				: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 };
		}
	}
	if (patch.orientation && patch.orientation !== next.page.orientation) {
		// The stored width and height ARE the laid-out ones (the API turns the
		// sheet at write time), so turning it here swaps them.
		page = { ...page, width: next.page.height, height: next.page.width };
	}
	if (patch.margins) page.margins = { ...next.page.margins, ...patch.margins };

	// A unit change carries the X-dimension with it. Every other coordinate
	// is a box an author can see is wrong; a 0.013 that meant inches and now
	// means millimetres is a symbol thirty times too small, which is both
	// invisible in the editor and refused by the API. (Read BEFORE the new
	// page is installed, or there is nothing left to compare against.)
	const unitChanged = page.unit !== next.page.unit;
	next.page = page;
	const convert = value => (page.unit === 'mm' ? value * 25.4 : value / 25.4);
	next.blocks = next.blocks.map(block => {
		const w = Math.min(block.w, page.width);
		const moved = { ...block, w: round(w, page.unit), x: clampX(block.x, w, page), y: clampY(block.y, page) };
		if (moved.type === 'group') return { ...scaleGroup(block, moved.w, block.h, page.unit), x: moved.x, y: moved.y };
		if (moved.type === 'barcode') {
			const size = unitChanged && typeof block.module === 'number' ? convert(block.module) : block.module;
			const floor = moduleFloor(moved.symbology, page.unit);
			moved.module = roundModule(Math.max(floor, typeof size === 'number' ? size : floor), page.unit);
		}
		return moved;
	});
	return next;
}

/** The subject, and the query a query-subject layout starts from. */
export function setSubject(body, subject) {
	const next = clone(body);
	next.subject = subject;
	if (subject === 'record') delete next.query;
	else if (next.query === undefined) next.query = '';
	// A linkElement points back at the record being printed, so it means
	// nothing once the layout starts from a query of its own.
	if (subject === 'query') {
		next.blocks = next.blocks.map(block => {
			if (block.type !== 'query' || !block.linkElement) return block;
			const { linkElement, ...rest } = block;
			return rest;
		});
	}
	return next;
}

/** What the API will be sent — the editor's model IS the wire shape. */
export const toBody = body => clone(body);
